import { useNavigation } from '@react-navigation/native'
import { useCallback, useMemo } from 'react'
import { StackNavigationProp } from '@react-navigation/stack'
import { useSelector } from 'react-redux'
import { AppStackParamList } from '@src/navigation/types'
import { getCoachingKindDescriptor } from '@src/utils/coach'
import {
  CoachesCoachingKind,
  EhrAppointmentAttendanceChargeKind,
  EhrAppointmentSchedulingMode,
  UiStateNames,
} from '@src/types'
import { useDispatchAsync } from '@src/utils/dispatch'
import { priorityOrderedCoachAssignmentsSelector } from '../../models/nutritionistHub.selectors'
import { useStressManagementSchedulingInfo } from './useStressManagementSchedulingInfo'

interface StressManagementOfferingHookParams {
  skipLoadingSchedulingInfo?: boolean
}

export const useStressManagementOffering = ({
  skipLoadingSchedulingInfo = false,
}: StressManagementOfferingHookParams = {}) => {
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const dispatchAsync = useDispatchAsync()

  const coachAssignments = useSelector(priorityOrderedCoachAssignmentsSelector) || []

  const hasAssignedCoach = !!coachAssignments.find(
    (assignment) => assignment.coachingKind.key === CoachesCoachingKind.StressManagement,
  )?.coach

  const stressCoachDescriptor = getCoachingKindDescriptor(CoachesCoachingKind.StressManagement)
  const stressCoachTitle = stressCoachDescriptor.capitalized.toString()
  const stressCoachPluralTitle = stressCoachDescriptor.capitalized.plural.toString()

  const { schedulingInfo, loading, error } = useStressManagementSchedulingInfo({
    skip: skipLoadingSchedulingInfo,
  })
  const enabled =
    !!schedulingInfo && schedulingInfo.schedulingMode !== EhrAppointmentSchedulingMode.None
  const isMntOffering =
    schedulingInfo?.schedulingMode === EhrAppointmentSchedulingMode.InsuranceCovered
  const productOptions = schedulingInfo?.options
  const { product: prepaidProduct, payAsYouGoProduct } = productOptions?.[0] ?? {}
  const product =
    schedulingInfo?.chargeKind === EhrAppointmentAttendanceChargeKind.BsSelfPay
      ? payAsYouGoProduct
      : prepaidProduct

  const bannerProps = useMemo(() => {
    if (loading) {
      return { title: '', subtitle: '' }
    }

    return {
      title: 'Feeling stressed?',
      subtitle: isMntOffering
        ? 'Covered by your insurance: 60-min stress session with a coach'
        : `Book a 60-min stress management session for $${product?.price}.`,
    }
  }, [product, isMntOffering, loading])

  const onContinue = useCallback(() => {
    if (!schedulingInfo) {
      return
    }
    const { options, pendingAppointment, chargeKind } = schedulingInfo
    const appointmentKind = options[0].appointmentKind

    if (
      isMntOffering ||
      chargeKind === EhrAppointmentAttendanceChargeKind.BsSelfPay ||
      !!pendingAppointment
    ) {
      if (hasAssignedCoach) {
        navigation.navigate('CallScheduling', {
          allowBackNavigation: true,
          appointmentKind,
          pendingAppointmentId: pendingAppointment?.id,
          product,
        })
        return
      }

      navigation.navigate('CallSchedulingSeeAllCoachesScreen', {
        coachingKind: CoachesCoachingKind.StressManagement,
        navigationTitle: `Choose Your ${stressCoachTitle}`,
        title: `Available ${stressCoachPluralTitle}`,
        loadMoreButtonText: 'Load More',
        showFilters: false,
        callSchedulingParams: {
          allowBackNavigation: true,
          appointmentKind,
          pendingAppointmentId: pendingAppointment?.id,
          product,
        },
      })
      return
    }

    if (!product) {
      return
    }

    navigation.navigate('VideoCallCheckout', { product })
  }, [
    isMntOffering,
    navigation,
    product,
    schedulingInfo,
    stressCoachPluralTitle,
    stressCoachTitle,
    hasAssignedCoach,
  ])

  const onDoNotShowAgain = useCallback(async () => {
    await dispatchAsync({
      type: 'app/upsertUiState',
      payload: {
        name: UiStateNames.StressManagementBannerDismissed,
        value: true,
      },
    })
    await dispatchAsync({ type: 'uiFlags/fetch' })
  }, [dispatchAsync])

  return {
    enabled,
    isMntOffering,
    product,
    bannerProps,
    loading,
    error,
    onContinue,
    onDoNotShowAgain,
  }
}
